<template>
  <AnalyticsWidget
    title="Stock Summary"
    subTitle="Summarised data for this stock item"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">

    <LoadingPanel :loading="loading" />
    <div v-if="!loading" class="p-2">
      <AnalyticsDashboardValueRow>
        <div class="col">
          <AnalyticsDashboardValue
            title="Total Sheets"
            :value="totalFullSheets">
            {{totalFullSheets}}
            <template v-slot:info>Total full sheets of this stock</template>
          </AnalyticsDashboardValue>
        </div>
        <div class="col">
          <AnalyticsDashboardValue
            title="Total Blocks"
            :value="totalBlocks">
            {{totalBlocks}}
            <template v-slot:info>Total unopened blocks of this stock</template>
          </AnalyticsDashboardValue>
        </div>
        <div class="col">
          <AnalyticsDashboardValue
            title="Total Value"
            :value="totalStockOnHandValue">
            {{formatCurrency(totalStockOnHandValue)}}
            <template v-slot:info>Total value of this stock</template>
          </AnalyticsDashboardValue>
        </div>
        <div class="col">
          <AnalyticsDashboardValue
            title="Incoming Stock"
            :value="totalIncomingStock">
            {{totalIncomingStock}}
            <template v-slot:info>Total sheets on order via purchase orders</template>
          </AnalyticsDashboardValue>
        </div>
      </AnalyticsDashboardValueRow>
      <AnalyticsDashboardValueRow>
        <div class="col">
          <AnalyticsDashboardValue
            title="Total Inventory"
            :value="totalPercentageOfTotalArea">
            {{formatPercent(totalPercentageOfTotalArea)}}
            <template v-slot:info>Percentage this stock represents of the entire inventory</template>
          </AnalyticsDashboardValue>
        </div>
        <div class="col">
          <AnalyticsDashboardValue
            title="Total Inventory Value"
            :value="totalPercentageOfTotalValue">
            {{formatPercent(totalPercentageOfTotalValue)}}
            <template v-slot:info>Percentage this stock represents of the entire inventory value</template>
          </AnalyticsDashboardValue>
        </div>
        <div class="col">
          <AnalyticsDashboardValue
            title="Average Sale Price"
            :value="averageSalePrice">
            <Spinner v-if="loadingSalesData" />
            <span v-if="!loadingSalesData">
              {{formatCurrency(averageSalePrice)}} / m&sup2;
            </span>
            <template v-slot:info>Based on completed orders for the last 30 days</template>
          </AnalyticsDashboardValue>
        </div>
        <div class="col">
          <AnalyticsDashboardValue
            title="Total Area Sold"
            :value="totalAreaSold">
            <Spinner v-if="loadingSalesData" />
            <span v-if="!loadingSalesData">
              {{formatDecimal(totalAreaSold, 2, true)}} m&sup2;
            </span>
            <template v-slot:info>Based on completed orders for the last 30 days</template>
          </AnalyticsDashboardValue>
        </div>
      </AnalyticsDashboardValueRow>
    </div>
  </AnalyticsWidget>
</template>


<script>
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'

export default {
  name: 'InventoryStockGlassSummaryWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValue,
    AnalyticsDashboardValueRow
  },
  props: {
    filter: Object,
    stockId: Number,
    gridWidth: Number,
    gridHeight: Number
  },
  computed: {
    totalFullSheets() {
      return this.glassSizeSummaryList.reduce((sum, item) => sum + item.totalFullSheets, 0)
    },
    totalBlockSheets() {
      return this.glassSizeSummaryList.reduce((sum, item) => sum + item.totalBlockSheets, 0)
    },
    totalBlocks() {
      return this.glassSizeSummaryList.reduce((sum, item) => sum + item.totalBlocks, 0)
    },
    totalOffcuts() {
      return this.glassSizeSummaryList.reduce((sum, item) => sum + item.totalOffcuts, 0)
    },
    totalArea() {
      return this.glassSizeSummaryList.reduce((sum, item) => sum + item.totalArea, 0)
    },
    totalStockOnHandValue() {
      return this.glassSizeSummaryList.reduce((sum, item) => sum + item.totalStockOnHandValue, 0)
    },
    totalPercentageOfTotalArea() {
      return this.glassSizeSummaryList.reduce((sum, item) => sum + item.percentageOfTotalArea, 0)
    },
    totalPercentageOfTotalValue() {
      return this.glassSizeSummaryList.reduce((sum, item) => sum + item.percentageOfTotalValue, 0)
    },
    totalIncomingStock() {
      return this.glassSizeSummaryList.reduce((sum, item) => sum + item.incomingStock, 0)
    },
    averageSalePrice() {
      if(this.salesDetailedList.stocks) {
        let stockSalesInfo = this.salesDetailedList.stocks.find(stockSalesInfo => stockSalesInfo.id == this.stockId)
        if(stockSalesInfo) {
          return stockSalesInfo.avgSalePrice
        }
      }
      return 0
    },
    totalAreaSold() {
      if(this.salesDetailedList.stocks) {
        let stockSalesInfo = this.salesDetailedList.stocks.find(stockSalesInfo => stockSalesInfo.id == this.stockId)
        if(stockSalesInfo) {
          return stockSalesInfo.totalUnits
        }
      }
      return 0
    }
  },
  data() {
    return {
      loading: false,
      glassSizeSummaryList: [],

      loadingSalesData: false,
      salesDetailedList: {}
    }
  },
  watch: {
    stockId: function() {
      this.requestData()
    }    
  },
  mounted() {
    this.requestData()
  },
  methods: {
    requestData() {
      var summaryParams = {
        usageStartDate: moment().local().subtract(12, 'months').startOf('day').toDate().toISOString(),
        usageEndDate: moment().local().endOf('day').toDate().toISOString(),
        stockId: this.stockId
      }
      

      this.loading = true
      this.$apiService.analyticsService.getGlassSizeSummary(summaryParams)
      .then(response => {
        // Process the response data
        this.glassSizeSummaryList = response.data
      })
      .finally(() => {
        this.loading = false
      })


      // Load sales data. Loaded seperately as this takes longer & we can display other data while waiting
      // Loads last 30 days of sales data
      var salesParams = {
        startDate: moment().local().subtract(30, 'days').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
        contactId: null,
        stockIdList: [this.stockId]
      }

      this.loadingSalesData = true
      this.$apiService.analyticsService.getOrdersValueGlassDetailedList(salesParams)
      .then(response => {
        this.salesDetailedList = response.data
      })
      .finally(() => {
        this.loadingSalesData = false
      })
    }
  }
}
</script>